import { postWithAuth } from '@/lib/httpAdapter';
import apiUrl from './api';
import User from '@/types/userType';
import headerObject from '@/types/headerObj';
import useUserContextStore from '@/contexts/UserContext';

const localHost = apiUrl;

const endPoint = 'fetchUser/';
const url = localHost + endPoint;

const fetchUser = async (
  UserUUID: string,
  headerObj?: headerObject
): Promise<User | undefined> => {
  if (!UserUUID) return undefined;

  try {
    const res = await postWithAuth(
      url,
      {
        UserUUID,
      },
      {
        headers: {
          ...headerObj,
        },
      }
    );

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

    const text = await res.json();
    const data = JSON.parse(text);
    return data.User;
  } catch (err) {
    console.error(err);
  }
};

// Throwing variant: a failed request (backend down, 5xx, 401) THROWS instead
// of returning undefined, so callers can tell "the backend said this user does
// not exist" apart from "the backend could not be asked". Returning undefined
// for both is what sent logged-in users to /onboarding whenever the backend
// was unreachable.
export const fetchUserByEmailOrThrow = async (
  Email: string,
  headerObj?: headerObject,
  providerOverride?: string
): Promise<User | undefined> => {
  if (!Email) return undefined;

  const provider =
    providerOverride ?? useUserContextStore.getState().user?.provider;

  const res = await postWithAuth(
    url,
    {
      Email,
      Provider: provider,
    },
    {
      headers: {
        ...headerObj,
      },
    }
  );

  if (!res.ok) {
    const error = new Error(`HTTP error! status: ${res.status}`) as Error & {
      status?: number;
    };
    error.status = res.status;
    throw error;
  }

  const data = await res.json();

  if (typeof data === 'string') {
    const parsedData = JSON.parse(data);
    return parsedData.User;
  }
  return data.User;
};

export const fetchUserByEmail = async (
  Email: string,
  headerObj?: headerObject,
  providerOverride?: string
): Promise<User | undefined> => {
  try {
    return await fetchUserByEmailOrThrow(Email, headerObj, providerOverride);
  } catch (err) {
    console.error(err);
  }
};

export const fetchUserByGithubUsername = async (
  GithubUsername: string,
  headerObj?: headerObject
): Promise<User | undefined> => {
  if (!GithubUsername) return undefined;

  try {
    const res = await postWithAuth(
      url,
      {
        GithubUsername,
      },
      {
        headers: {
          ...headerObj,
        },
      }
    );

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

    const data = await res.json();
    if (typeof data === 'string') {
      const parsed = JSON.parse(data);
      return parsed.User;
    }
    return data.User;
  } catch (err) {
    console.error(err);
  }
};

export const fetchUserByGitlabUsername = async (
  GitlabUsername: string,
  headerObj?: headerObject
): Promise<User | undefined> => {
  if (!GitlabUsername) return undefined;

  try {
    const res = await postWithAuth(
      url,
      {
        GitlabUsername,
      },
      {
        headers: {
          ...headerObj,
        },
      }
    );

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

    const data = await res.json();
    if (typeof data === 'string') {
      const parsed = JSON.parse(data);
      return parsed.User;
    }
    return data.User;
  } catch (err) {
    console.error(err);
  }
};

export const fetchUserByBitbucketUsername = async (
  BitbucketUsername: string,
  headerObj?: headerObject
): Promise<User | undefined> => {
  if (!BitbucketUsername) return undefined;

  try {
    const res = await postWithAuth(
      url,
      {
        BitbucketUsername,
      },
      {
        headers: {
          ...headerObj,
        },
      }
    );

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

    const data = await res.json();
    if (typeof data === 'string') {
      const parsed = JSON.parse(data);
      return parsed.User;
    }
    return data.User;
  } catch (err) {
    console.error(err);
  }
};

/**
 * Look a user up by GitHub's numeric account id.
 *
 * The id outlives the login and the email, both of which an enterprise identity
 * provider can rewrite, so this resolves the right record when the other two
 * have drifted.
 */
export const fetchUserByGithubUserId = async (
  GithubUserId: string,
  headerObj?: headerObject
): Promise<User | undefined> => {
  if (!GithubUserId) return undefined;

  try {
    const res = await postWithAuth(
      url,
      {
        GithubUserId,
      },
      {
        headers: {
          ...headerObj,
        },
      }
    );

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

    const data = await res.json();
    if (typeof data === 'string') {
      const parsed = JSON.parse(data);
      return parsed.User;
    }
    return data.User;
  } catch (err) {
    console.error(err);
  }
};

export const fetchUserByProviderUsername = async (
  username: string,
  provider: string | undefined,
  headerObj?: headerObject,
  providerUserId?: string
): Promise<User | undefined> => {
  if (!username && !providerUserId) return undefined;

  const normalizedProvider = (provider ?? '').toLowerCase();

  if (normalizedProvider === 'github') {
    return (
      (await fetchUserByGithubUserId(providerUserId ?? '', headerObj)) ??
      (await fetchUserByGithubUsername(username, headerObj))
    );
  }
  if (normalizedProvider === 'gitlab') {
    return fetchUserByGitlabUsername(username, headerObj);
  }
  if (normalizedProvider === 'bitbucket') {
    return fetchUserByBitbucketUsername(username, headerObj);
  }

  return (
    (await fetchUserByGithubUsername(username, headerObj)) ??
    (await fetchUserByGitlabUsername(username, headerObj)) ??
    (await fetchUserByBitbucketUsername(username, headerObj))
  );
};

/** Session fields needed to resolve a durable identity. */
export type SessionIdentity = {
  id?: string;
  email?: string | null;
  username?: string;
  ghUserName?: string;
  provider?: string;
};

/**
 * Resolve the logged-in person from a NextAuth session.
 *
 * GitHub: numeric account id (then username) before email, so Enterprise
 * Managed User IdP address drift still hits the right row. Other providers
 * keep the email path.
 */
export const fetchUserFromSession = async (
  sessionUser: SessionIdentity | null | undefined,
  headerObj?: headerObject
): Promise<User | undefined> => {
  if (!sessionUser) return undefined;

  if ((sessionUser.provider ?? '').toLowerCase() === 'github') {
    const byGithub = await fetchUserByProviderUsername(
      sessionUser.username ?? sessionUser.ghUserName ?? '',
      sessionUser.provider,
      headerObj,
      sessionUser.id
    );
    if (byGithub?.UserUUID) return byGithub;
  }

  if (!sessionUser.email) return undefined;
  return fetchUserByEmail(sessionUser.email, headerObj);
};

/**
 * Search for users by GitHub or GitLab username (partial, case-insensitive).
 * @param {Object} params - Search parameters
 * @param {string} [params.github_username] - Partial or full GitHub username
 * @param {string} [params.gitlab_username] - Partial or full GitLab username
 * @returns {Promise<any | undefined>} The API response or undefined on error
 */
export async function searchUsers(
  params: { github_username?: string; gitlab_username?: string },
  headerObj?: headerObject
) {
  const endpoint = apiUrl + 'searchUsers/';
  try {
    const res = await postWithAuth(endpoint, params, {
      headers: {
        ...headerObj,
      },
    });

    if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

    const text = await res.json();
    const data = typeof text === 'string' ? JSON.parse(text) : text;
    return data;
  } catch (err) {
    console.error('Error searching users:', err);
    return undefined;
  }
}

export default fetchUser;
